import {
  Box,
  Button,
  HStack,
  PopoverContent,
  PopoverContentProps,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NotificationInterface } from "../../constants/Notification";
import { CurrentActiveUserInterface } from "../../constants/User";
import { goToDashboardFromNotif } from "../../hooks/fetchApi";
import useCustomToast from "../../lib/useCustomToast";
import { useTriggerRenderStore } from "../../store/useTriggerRenderStore";
import { useUserHeader } from "../../store/useUserHeader";
import {
  useDarkWhiteColor,
  useDividerColor,
  useRedSoftPrimary,
} from "../../themes/colors";
import { getAxiosInstance } from "../../utils/helperFunction";
import { Empty } from "../card/Empty";

interface Props extends PopoverContentProps {
  isOpen: boolean;
  currentActiveUser: CurrentActiveUserInterface | null;
}

export const PopoverNotification = ({
  isOpen,
  currentActiveUser,
  ...rest
}: Props) => {
  const [data, setData] = useState<NotificationInterface[] | undefined>(
    undefined
  );
  const [loaded, setLoaded] = useState<boolean>(false);
  const { statusData, setStatusData } = useTriggerRenderStore();
  const { fetchNotificationCount } = useUserHeader();
  const bgBnw = useDarkWhiteColor();
  const redSoft = useRedSoftPrimary();
  const navigate = useNavigate();
  const toast = useCustomToast();
  const dividerColor = useDividerColor();

  useEffect(() => {
    if (isOpen) {
      let pageType = 1;

      if (currentActiveUser) {
        if (currentActiveUser.role === "reseller") {
          pageType = 2;
        } else if (currentActiveUser.role === "supplier") {
          pageType = 3;
        }

        const axiosInstance = getAxiosInstance(pageType);

        axiosInstance
          .get(`/v1/${currentActiveUser.role}/notification`)
          .then((res) => {
            setData(res.data.data);
          })
          .catch((err) => {
            toast({
              description: err?.response?.data?.message || "",
            });
          })
          .finally(() => {
            setLoaded(true);
          });
      }
    }
  }, [isOpen, currentActiveUser, statusData]);

  const markAllRead = () => {};

  const onRefresh = () => {
    fetchNotificationCount();
    setStatusData();
  };

  const handleClickNotif = (id: any, link: string, isRead: number) => {
    goToDashboardFromNotif(
      id,
      link,
      isRead,
      navigate,
      currentActiveUser,
      toast,
      onRefresh
    );
  };

  return (
    <PopoverContent
      rounded={0}
      border={"none"}
      w={"auto"}
      shadow={"none"}
      {...rest}
    >
      <Box
        w={"240px"}
        roundedBottom={"8px"}
        maxH={"80vh"}
        overflowY={"auto"}
        bg={bgBnw}
        mr={{ base: "8px", md: "8px", xl: 0 }}
        shadow={"md"}
      >
        <Text textStyle={"b-sm-sb"} px={"15px"} py={"8px"}>
          Notifikasi
        </Text>

        {!loaded ? (
          <SkeletonText height="40px" noOfLines={2} />
        ) : data && data.length > 0 ? (
          data.toReversed().map((notification, index) => (
            <Box
              key={index}
              p={"15px"}
              bg={notification.isRead === 0 ? redSoft : bgBnw}
              cursor={"pointer"}
              borderBottomWidth={"1px"}
              borderBottomColor={dividerColor}
              onClick={() =>
                handleClickNotif(
                  notification.id,
                  notification.link,
                  notification.isRead
                )
              }
            >
              <Text fontSize={[10, null, 12]} fontWeight={"semibold"}>
                {notification.title}
              </Text>
              <Text fontSize={[8, null, 10]}>{notification.body}</Text>
              <Text
                fontSize={[8, null, 10]}
                variant={"secondary"}
                textAlign={"right"}
              >
                {getDateTime(notification.created_at)}
              </Text>
            </Box>
          ))
        ) : (
          <VStack p={12}>
            <Empty />
          </VStack>
        )}

        <HStack
          px={"15px"}
          justifyContent={"space-between"}
          position={"sticky"}
          bottom={0}
          bg={bgBnw}
        >
          {data && data.length > 0 && (
            <Button
              variant={"ghost"}
              fontSize={"8px"}
              size={"md"}
              onClick={markAllRead}
            >
              Tandai Baca Semua
            </Button>
          )}
        </HStack>
      </Box>
    </PopoverContent>
  );
};

const getDateTime = (date: string | Date) => {
  const dateObj = new Date(date);
  if (new Date().getTime() - dateObj.getTime() < 60000) {
    return "Baru saja";
  } else if (new Date().getDate() - dateObj.getDate() < 1) {
    return dateObj.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } else if (new Date().getDate() - dateObj.getDate() === 1) {
    return "Kemarin";
  }
  return dateObj.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};
